import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { withTranslation } from 'react-i18next'
import { LoadingSpinner } from '../../../../../legacy/views/helpers/loading/spinner'
import { apiService } from '../../../../api/api.service'
import {
  StyledWrapper,
  StyledSubheading,
  AdditionalInformation,
} from '../../order-form.component.styles'
import {
  UploadedImageContainer,
  UploadedImage,
  RemoveImageButton,
  StyledImageUploaderImage,
  UploaderWrapper,
  StyledButton,
  StyledError,
} from './package-photo.component.styles'

import { getDeliveryOrderDetailImageUrl } from '../../../../duck/selectors'
import { deliverySetOrderDetailImageUrl } from '../../../../duck/actions'
import { orderTrackingService } from '../../../tracking.service'

import { Dropzone } from 'ggx-componentlibrary/components/dropzone/dropzone.component'
import { SmallText } from 'ggx-componentlibrary/design/typography/typography.styles'

class PackagePhoto extends React.Component {
  static propTypes = {
    input: PropTypes.shape({
      value: PropTypes.string,
      onChange: PropTypes.func.isRequired,
    }),
    orderDetailImageUrl: PropTypes.string,
    t: PropTypes.func,
  }

  state = {
    uploadedImageURL: null,
    uploadedImage: null,
    imageUploading: false,
    uploadError: false,
  }

  componentDidMount() {
    const { orderDetailImageUrl } = this.props
    this.handleOrderDetailImageUrl(orderDetailImageUrl)
  }

  componentDidUpdate(prevProps) {
    const { orderDetailImageUrl } = this.props
    if (orderDetailImageUrl !== prevProps.orderDetailImageUrl) {
      this.handleOrderDetailImageUrl(orderDetailImageUrl)
    }
  }

  handleOrderDetailImageUrl = async imageUrl => {
    if (imageUrl?.startsWith('data:image')) {
      const image = await this._convertOrderDetailImageUrl(imageUrl)
      let file = image

      // POST images endpoint expects file not blob
      if (image.name === undefined) {
        const type = image.type.split('/')[1]
        file = new File([image], `image.${type}`, { type })
      }

      this.setState({ uploadedImage: file })
      this.handleSelectFile(file, 'populate')
    } else if (imageUrl?.startsWith('https://')) {
      this.setState({ uploadedImageURL: imageUrl })
      this.props.input.onChange(imageUrl.split('/')[4])
    } else {
      this.setState({ uploadedImage: null, uploadedImageURL: null })
      this.props.input.onChange('')
    }
  }

  handleSelectFile = async (file, type) => {
    const { onChange } = this.props.input
    const { dispatchClearImageDataUrl, t } = this.props

    if (file) {
      try {
        this.setState({ uploadError: false })
        this.setState({ imageUploading: true })
        const { secret } = await apiService.uploadImage({ file })
        this.setState({ uploadedImage: file })
        orderTrackingService.trackPackagePhotoUpload({
          imageUploaded: true,
          uploadType: type,
        })
        return onChange(secret)
      } catch (error) {
        this.setState({ uploadError: t('error.photo.uploadFail') })
        orderTrackingService.trackPackagePhotoUpload({
          imageUploaded: false,
          uploadType: type,
          uploadError: 'network-error',
        })
      } finally {
        this.setState({ imageUploading: false })
      }
    } else {
      dispatchClearImageDataUrl()
      this.setState({ uploadedImage: null, uploadedImageURL: null })
      return onChange('')
    }
  }

  _convertOrderDetailImageUrl = async imageUrl => {
    const res = await fetch(imageUrl)
    return res.blob()
  }

  handleRemoveImage = () => {
    this.handleSelectFile(null)
    window.URL.revokeObjectURL(this.state.uploadedImage)
  }

  handleFileRejected = (file, type) => {
    const { t } = this.props
    const errorCode = file?.[0].errors?.[0].code
    const errorType = [
      { code: 'file-too-large', message: t('error__ggd_upload_file_size') },
      { code: 'file-invalid-type', message: t('error__ggd_upload_file_type') },
      { code: 'too-many-files', message: t('error__too_many_image_files') },
    ]
    const mappedError =
      errorType.find(error => error.code === errorCode)?.message ||
      t('error.photo.uploadFail')
    this.setState({ uploadError: mappedError })
    orderTrackingService.trackPackagePhotoUpload({
      imageUploaded: false,
      uploadType: type,
      uploadError: errorCode,
    })
  }

  render() {
    const { uploadedImage, uploadedImageURL, imageUploading, uploadError } = this.state
    const { t } = this.props
    const imageUrl = uploadedImageURL ||
      (uploadedImage && window.URL.createObjectURL(uploadedImage))

    const DropzoneContent = ({ open }) => (
      <>
        <StyledImageUploaderImage />
        <SmallText>
          {t('orders.new.basicInfo.subtitle.packagePhotoLimitation')}
        </SmallText>
        <StyledButton buttonType="secondary" onClick={open}>
          {t('orders.new.basicInfo.button.uploadPhoto')}
        </StyledButton>
      </>
    )

    return (
      <StyledWrapper>
        <StyledSubheading>
          {t('orders.new.basicInfo.subtitle.packagePhoto')}
        </StyledSubheading>
        <AdditionalInformation>
          {t('orders.new.basicInfo.subtitle.packagePhotoDescription')}
        </AdditionalInformation>
        <UploaderWrapper>
          {imageUrl ? (
            <UploadedImageContainer>
              <UploadedImage src={imageUrl} alt="" />
              <RemoveImageButton
                onClick={this.handleRemoveImage}
                title={t('orders.new.basicInfo.button.removePhoto')}
                aria-label={t('orders.new.basicInfo.button.removePhoto')}
              />
            </UploadedImageContainer>
          ) : (
            <Dropzone
              fileType="image/png, image/jpeg"
              handleFileUpload={(event, type) =>
                this.handleSelectFile(event.target.files[0], type)
              }
              handleRejected={this.handleFileRejected}
              loading={imageUploading}
              height="255px"
              customLoadingIndicator={<LoadingSpinner />}
            >
              <DropzoneContent />
            </Dropzone>
          )}
          {uploadError && !imageUrl && <StyledError>{uploadError}</StyledError>}
        </UploaderWrapper>
      </StyledWrapper>
    )
  }
}

const mapStateToProps = state => ({
  orderDetailImageUrl: getDeliveryOrderDetailImageUrl(state),
})

const mapDispatchToProps = dispatch => ({
  dispatchClearImageDataUrl: () =>
    dispatch(deliverySetOrderDetailImageUrl(null)),
})

const PackagePhotoWithTranslation = withTranslation()(PackagePhoto)
const ConnectedPackagePhotoWithTranslation = connect(
  mapStateToProps,
  mapDispatchToProps
)(PackagePhotoWithTranslation)

export { PackagePhotoWithTranslation, ConnectedPackagePhotoWithTranslation }
